import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { IconButton } from '../Button';
import VipIcon from '../../Icons/VipIcons';
import { SEARCH } from '../../../constants/staticTexts';

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SearchBarContainer = styled.div`
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 45px;
  border: 2px solid ${({theme}) => theme.colors.divider};
  border-radius: 25px;
  background: ${({theme}) => theme.colors.bg1};
  color: ${({theme}) => theme.colors.text};
  font-size: 16px;
  transition: all 0.2s ease;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: ${({theme}) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: ${({theme}) => theme.colors.textSecondary};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: ${({theme}) => theme.colors.bg3};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({theme}) => theme.colors.textSecondary};
  pointer-events: none;
`;

const ClearButton = styled(IconButton).attrs({ $size: 'small' })`
  position: absolute;
  right: 11px;
  top: calc(50% - 16px);
  color: ${({theme}) => theme.colors.textSecondary};

  &:hover:not(:disabled) {
    color: ${({theme}) => theme.colors.text};
  }
`;

const SearchStatus = styled.div`
  padding: 12px 16px;
  font-size: 14px;
  color: ${({theme}) => theme.colors.textSecondary};
  background: ${({theme}) => theme.colors.bg2};
  border-radius: 12px;
  margin-top: 8px;
  text-align: center;
  border: 1px solid ${({theme}) => theme.colors.divider};
`;

const LoadingIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  color: ${({theme}) => theme.colors.textSecondary};
  font-size: 14px;
  background: ${({theme}) => theme.colors.bg2};
  border-radius: 12px;
  margin-top: 8px;
  border: 1px solid ${({theme}) => theme.colors.divider};
`;

const SearchComponent = ({
  placeholder = SEARCH.DEFAULT_PLACEHOLDER,
  value = "",
  onChange,
  onSearch,
  onClear,
  debounceMs = 500,
  minLength = 2,
  showStatus = true,
  loading = false,
  error = null,
  className,
  autoFocus = false,
  disabled = false,
  ...props
}) => {
  const [searchTerm, setSearchTerm] = useState(value);
  const [isTyping, setIsTyping] = useState(false);
  const debounceTimer = useRef(null);
  const inputRef = useRef(null);

  // Update internal state when external value changes
  useEffect(() => {
    setSearchTerm(value);
  }, [value]);

  // Debounced search function
  const debouncedSearch = useCallback((term) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setIsTyping(false);
      if (onSearch && term.length >= minLength) {
        onSearch(term);
      }
    }, debounceMs);
  }, [onSearch, debounceMs, minLength]);

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsTyping(true);

    // Call onChange immediately for controlled components
    if (onChange) {
      onChange(e);
    }

    // Trigger debounced search
    if (newValue.length >= minLength || newValue.length === 0) {
      debouncedSearch(newValue);
    }
  };

  // Handle clear
  const handleClear = () => {
    setSearchTerm("");
    setIsTyping(false);
    
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (onClear) {
      onClear();
    } else if (onChange) {
      // Create a synthetic event for onChange
      const syntheticEvent = {
        target: { value: "" },
        preventDefault: () => {},
        stopPropagation: () => {}
      };
      onChange(syntheticEvent);
    }

    if (onSearch) {
      onSearch("");
    }

    // Focus back on input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      setIsTyping(false);
      if (onSearch) {
        onSearch(searchTerm);
      }
    } else if (e.key === 'Escape') {
      handleClear();
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  // Get status message
  const getStatusMessage = () => {
    if (error) return error;
    if (loading || isTyping) return SEARCH.SEARCHING;
    if (searchTerm.length > 0 && searchTerm.length < minLength) {
      return `${SEARCH.MIN_CHARS_PREFIX} ${minLength} ${SEARCH.MIN_CHARS_SUFFIX}`;
    }
    return null;
  };

  const statusMessage = getStatusMessage();
  const showClearButton = searchTerm.length > 0 && !loading && !disabled;

  return (
    <SearchContainer className={className}>
      <SearchBarContainer>
        <SearchIcon>
          <VipIcon name="Search" size={16} />
        </SearchIcon>
        
        <SearchInput
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyPress}
          autoFocus={autoFocus}
          disabled={disabled}
          {...props}
        />
        
        {showClearButton && (
          <ClearButton onClick={handleClear} type="button" aria-label="Clear search">
            <VipIcon name="CloseXCircle" size={14} />
          </ClearButton>
        )}
      </SearchBarContainer>
      
      {loading && (
        <LoadingIndicator>
          <VipIcon name="Loader" size={16} />
          {SEARCH.SEARCHING}
        </LoadingIndicator>
      )}
      
      {showStatus && statusMessage && !loading && (
        <SearchStatus>
          {statusMessage}
        </SearchStatus>
      )}
    </SearchContainer>
  );
};

export default SearchComponent;
